import { useEffect, type MutableRefObject } from "react";
import type { LastBotCore } from "../core/LastBotCore";

/**
 * How far from the bottom still counts as "reading the latest message", in px.
 * Covers sub-pixel rounding and the last line's descenders.
 */
export const SCROLL_ANCHOR_SLACK = 24;

/**
 * How long to keep re-pinning after a resize, in ms.
 *
 * `src/index.css` animates the panel's width/height/max-height over 0.3s
 * ease-in-out, so the viewport keeps contracting long after the event fires:
 * a single deferred re-pin lands on the transition's FIRST frame and the latest
 * content drifts below the fold for the remaining ~290ms of a shrink. The
 * window covers the transition plus a margin for a delayed final frame, and is
 * hard-capped so the hook can never hold a pin indefinitely.
 */
export const SCROLL_ANCHOR_FOLLOW_MS = 450;

/**
 * Re-pins the message list to the bottom after the panel is resized.
 *
 * Streaming already follows the bottom: TypewriterEffect calls its onChange on
 * every reveal tick, which the assistant item wires to scrollToBottom. But a
 * resize driven by the answer's own content (LAS-2072 rich-media sizing) lands
 * once that answer has SETTLED — precisely when the last tick has already
 * fired. The viewport then shrinks under a scroll position nothing corrects,
 * leaving the just-written answer below the fold.
 *
 * Only re-pins when the reader was already at the bottom, so a resize cannot
 * yank someone who scrolled up to read history. Distance is measured when the
 * event arrives, i.e. before the browser has re-laid out for the new size, so
 * it reflects the pre-resize position — which is the one worth preserving.
 *
 * A qualifying event starts a bounded FOLLOW window (see
 * `SCROLL_ANCHOR_FOLLOW_MS`): a ResizeObserver on the scroll container re-pins
 * on every observed size change through the CSS transition. Following stops the
 * moment the container's scrollTop differs from the value the last pin left
 * behind — that difference is the user scrolling, and the hook must never fight
 * them. Where ResizeObserver is unavailable the hook degrades to the single
 * deferred re-pin it has always done.
 */
export const useScrollAnchorOnResize = (
  core: LastBotCore,
  scrollRef: MutableRefObject<HTMLDivElement | null>,
  /**
   * What a qualifying resize re-pins. Pages pass `restoreReadingPosition`,
   * which keeps a reading anchor where one is installed and pins the bottom
   * otherwise (LAS-1381).
   */
  scrollToBottom: () => void,
  /**
   * A reader whose question is anchored to the top of a long answer is far
   * from the bottom yet has not scrolled away, so the distance guard below
   * must not skip them. Defaults to "no anchor" for callers without one.
   */
  hasReadingAnchor: () => boolean = () => false,
): void => {
  useEffect(() => {
    let timerId: number | null = null;
    let capTimerId: number | null = null;
    let observer: ResizeObserver | null = null;
    let followed: HTMLElement | null = null;
    // The scroll position the last pin left behind — the baseline every
    // subsequent pin checks against to detect a user scroll.
    let pinnedScrollTop = 0;

    const stopFollowing = (): void => {
      if (observer !== null) {
        observer.disconnect();
        observer = null;
      }
      if (capTimerId !== null) {
        window.clearTimeout(capTimerId);
        capTimerId = null;
      }
      if (timerId !== null) {
        window.clearTimeout(timerId);
        timerId = null;
      }
      followed = null;
    };

    /**
     * Re-pins unless the reader moved. Returns false (and stops the window)
     * when the list went away or the scroll position is not where we left it.
     */
    const repin = (): boolean => {
      const container = scrollRef.current;
      if (container === null || container !== followed || container.scrollTop !== pinnedScrollTop) {
        stopFollowing();
        return false;
      }
      scrollToBottom();
      pinnedScrollTop = container.scrollTop;
      return true;
    };

    const handleSizeChange = (): void => {
      const container = scrollRef.current;
      if (!container) return;

      const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
      if (distanceFromBottom > SCROLL_ANCHOR_SLACK && !hasReadingAnchor()) return;

      // A fresh qualifying resize restarts the window on the current container.
      stopFollowing();
      followed = container;
      pinnedScrollTop = container.scrollTop;

      // After a tick, so the scroll height reflects the applied size. This is
      // also the whole behaviour where ResizeObserver is unavailable.
      timerId = window.setTimeout(() => {
        timerId = null;
        repin();
      }, 0);

      if (typeof ResizeObserver === "undefined") return;

      // Follow the transition: each observed box change is another frame of the
      // panel animating, and the bottom has to be re-pinned under it.
      observer = new ResizeObserver(() => {
        repin();
      });
      observer.observe(container);
      capTimerId = window.setTimeout(stopFollowing, SCROLL_ANCHOR_FOLLOW_MS);
    };

    const stopListening = core.on("widget:set-size", handleSizeChange);
    return (): void => {
      stopListening();
      stopFollowing();
    };
  }, [core, scrollRef, scrollToBottom, hasReadingAnchor]);
};
